/**
 * FirstRunMentalModelDialog — RC 1.49.x reviewer-trust polish.
 *
 * Operator-locked first-run splash that gives candidates a calm
 * mental model of the platform AFTER login. Strictly scoped to:
 *   - Define Analyse Fit.
 *   - Define Application Pack.
 *   - Sketch the basic flow.
 *   - State NDORFlow never auto-applies.
 *
 * Hard rails:
 *   - No walkthrough framework, no product-tour engine.
 *   - Does NOT block the user from using the app (it's a Dialog
 *     they can dismiss with Esc or the Close button).
 *   - Per-user localStorage dismissal key — so each candidate
 *     sees it once on first arrival, never again unless they
 *     reopen via the user menu.
 *   - No backend / schema / billing / auth touched.
 */

import { useCallback, useEffect, useState } from 'react';
import { Sparkles, Package, X as XIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

const DISMISS_KEY_PREFIX = 'ndor:first-run-mental-model:dismissed';

export function dismissKeyFor(userId) {
  return userId ? `${DISMISS_KEY_PREFIX}:${userId}` : `${DISMISS_KEY_PREFIX}:anon`;
}

export function isFirstRunDismissed(userId) {
  try {
    return window.localStorage.getItem(dismissKeyFor(userId)) === '1';
  } catch {
    return false;
  }
}

export function markFirstRunDismissed(userId) {
  try {
    window.localStorage.setItem(dismissKeyFor(userId), '1');
  } catch {
    /* localStorage disabled — silently no-op */
  }
}

export function clearFirstRunDismissal(userId) {
  try {
    window.localStorage.removeItem(dismissKeyFor(userId));
  } catch {
    /* no-op */
  }
}

export default function FirstRunMentalModelDialog({
  open,
  onOpenChange,
  userId,
  // When true, the "Don't show this again" checkbox is hidden —
  // used when the user re-opens the dialog manually from the
  // user-menu "Show first-run guide" entry.
  hideDismissCheckbox = false,
}) {
  const [dontShowAgain, setDontShowAgain] = useState(true);

  // Reset checkbox state every time the dialog opens.
  useEffect(() => {
    if (open) setDontShowAgain(true);
  }, [open]);

  const handleClose = useCallback(() => {
    if (!hideDismissCheckbox && dontShowAgain) {
      markFirstRunDismissed(userId);
    }
    onOpenChange?.(false);
  }, [dontShowAgain, hideDismissCheckbox, onOpenChange, userId]);

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (!next) handleClose();
      else onOpenChange?.(next);
    }}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="first-run-mental-model-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-serif-display tracking-tight">
            Welcome to NDORFlow
          </DialogTitle>
          {/* RC 1.49.x — premium product descriptor as the dialog
              subtitle. Replaces the older operational "short read"
              line so the modal opens with a clear, calm framing of
              what NDORFlow is. Hierarchy preserved (Radix
              DialogDescription default styling kept). */}
          <DialogDescription className="text-sm text-ndor-ink-secondary leading-relaxed">
            NDORFlow is a web-based, mobile-adapted decision intelligence workspace for managing job targeting, CV tailoring, and application preparation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* What Analyse Fit means */}
          <section
            className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/40 p-4"
            data-testid="first-run-analyse-fit-block"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-ndor-gold" />
              <h3 className="text-sm font-semibold text-ndor-ink">What Analyse Fit means</h3>
            </div>
            <p className="text-sm text-ndor-ink-secondary leading-relaxed">
              Analyse Fit compares your Source CV against a specific job description.
              It checks how well your current CV and profile evidence match the role
              before you decide whether to tailor your CV or create an application pack.
            </p>
            <p className="mt-2 text-xs text-ndor-ink-muted leading-relaxed">
              Your CV may look relevant by keywords, but Analyse Fit checks whether
              the evidence in your CV is strong enough for that particular role.
            </p>
            {/* RC 1.49.x — operational scroll-affordance hint for
                mobile users who might miss that the Analyse Fit
                report continues below the fold. Tone deliberately
                calm; no arrows, no emojis. */}
            <p className="mt-2 text-xs text-ndor-ink-muted leading-relaxed">
              Analyse Fit reports are scrollable — continue downward to review detailed strengths, gaps, and recommendations.
            </p>
          </section>

          {/* Basic flow */}
          <section data-testid="first-run-basic-flow-block">
            <h3 className="text-sm font-semibold text-ndor-ink mb-2">The basic flow</h3>
            <ol className="space-y-1.5 text-sm text-ndor-ink-secondary leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink shrink-0">1.</span>
                <span>Upload your Source CV.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink shrink-0">2.</span>
                <span>Add or choose a job.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink shrink-0">3.</span>
                <span>Run Analyse Fit.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink shrink-0">4.</span>
                <span>Decide whether to tailor your CV or generate a cover letter.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink shrink-0">5.</span>
                <span>Create an application pack.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-ndor-ink shrink-0">6.</span>
                <span>
                  <span className="font-medium text-ndor-ink">Submit yourself</span>
                  {' '}— NDORFlow never auto-applies.
                </span>
              </li>
            </ol>
          </section>

          {/* What an Application Pack contains */}
          <section
            className="rounded-lg border border-ndor-gold/30 bg-ndor-gold/5 p-4"
            data-testid="first-run-application-pack-block"
          >
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 text-ndor-gold" />
              <h3 className="text-sm font-semibold text-ndor-ink">What an Application Pack contains</h3>
            </div>
            <p className="text-sm text-ndor-ink-secondary leading-relaxed">
              A role-specific bundle containing the documents and intelligence you
              generated for that job, such as a tailored CV, cover letter, and
              interview-preparation insights.
            </p>
          </section>

          {/* Focus / Flow caveat */}
          <section
            className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/30 p-4"
            data-testid="first-run-focus-flow-block"
          >
            <h3 className="text-sm font-semibold text-ndor-ink mb-2">If you&rsquo;re on Focus or Flow</h3>
            {/* RC 1.49.x — body-copy contrast bumped one notch from
                ink-secondary → ink for mobile readability while
                preserving the rest of the modal's existing
                hierarchy. The fineprint line below stays one notch
                dimmer (ink-secondary) so the triple-negative
                disclaimer reads as a calm aside, not a headline. */}
            <p className="text-sm text-ndor-ink leading-relaxed">
              Focus and Flow users also get access to <span className="font-medium text-ndor-ink">Workflow</span> — a dedicated space for reviewing job matches and managing assisted application activity.
            </p>
            <p className="mt-2 text-sm text-ndor-ink leading-relaxed">
              On <span className="font-medium text-ndor-ink">Flow</span>, NDORFlow goes further: it can run background Analyse Fit checks across the jobs pool and surface your strongest matches at the top as <span className="font-medium text-ndor-ink">Top Opportunities</span>.
            </p>
            <p className="mt-2 text-xs text-ndor-ink-secondary leading-relaxed">
              You remain in control. NDORFlow never auto-tailors, never auto-applies, and never submits anything on your behalf.
            </p>
          </section>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2">
          {!hideDismissCheckbox ? (
            <label
              className="flex items-center gap-2 text-xs text-ndor-ink-secondary cursor-pointer select-none"
              data-testid="first-run-dont-show-again-label"
            >
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(v) => setDontShowAgain(v === true)}
                data-testid="first-run-dont-show-again-checkbox"
              />
              <span>Don&rsquo;t show this again</span>
            </label>
          ) : <span />}
          <Button
            onClick={handleClose}
            data-testid="first-run-got-it-button"
            className="w-full sm:w-auto"
          >
            <XIcon className="h-4 w-4 mr-1.5" /> Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
